import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { optimizeImageUrlFillRectangle } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { Anchor } from "@faire/web--source/ui/LinkAnchor";
import React from "react";
import styled from "styled-components";
import { generateCSSBreakpointMapForImages } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/utils";
export var CommunityCategoryBanner = /*#__PURE__*/ React.forwardRef(function(_props, ref) {
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onCTAClick = _React_useContext.onCTAClick, requestId = _React_useContext.requestId;
    return /*#__PURE__*/ _jsx(CommunityCategoryBannerBase, {
        layoutElement: layoutElement,
        onCTAClick: onCTAClick,
        requestId: requestId,
        ref: ref
    });
});
export var CommunityCategoryBannerBase = function(param) {
    var layoutElement = param.layoutElement, onCTAClick = param.onCTAClick, requestId = param.requestId, ref = param.ref, className = param.className;
    var isSSR = useIsSSR();
    var main_text = layoutElement.main_text, images = layoutElement.images, title_icon = layoutElement.title_icon, description_text = layoutElement.description_text, cta_text = layoutElement.cta_text, cta_link = layoutElement.cta_link, background_color = layoutElement.background_color, main_text_color = layoutElement.main_text_color, cta_text_color = layoutElement.cta_text_color, on_click_event_name = layoutElement.on_click_event_name;
    var handleClick = function() {
        onCTAClick === null || onCTAClick === void 0 ? void 0 : onCTAClick(on_click_event_name !== null && on_click_event_name !== void 0 ? on_click_event_name : "CommunityCategoryBanner", requestId !== null && requestId !== void 0 ? requestId : "");
    };
    return /*#__PURE__*/ _jsxs(Wrapper, {
        $backgroundColor: background_color,
        ref: ref,
        className: className,
        children: [
            /*#__PURE__*/ _jsxs(MessageContainer, {
                justify: "center",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "displaySSerifRegular",
                        color: main_text_color,
                        children: main_text
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: 14
                    }),
                    /*#__PURE__*/ _jsx(StyledRow, {
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraphSansRegular",
                            color: main_text_color,
                            children: description_text
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: 20
                    }),
                    /*#__PURE__*/ _jsx(StyledRow, {
                        children: /*#__PURE__*/ _jsx(StyledLink, {
                            onClick: handleClick,
                            target: "_blank",
                            href: cta_link,
                            children: /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansRegular",
                                color: cta_text_color,
                                children: cta_text
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(MessageImage, {
                $isSSR: isSSR,
                $images: generateCSSBreakpointMapForImages(images),
                children: title_icon ? /*#__PURE__*/ _jsx(StyledFairTradeFederationIcon, {
                    src: optimizeImageUrl(title_icon, isSSR, {
                        resizeMethod: "fit",
                        maintainTransparency: true
                    })
                }) : null
            })
        ]
    });
};
var Wrapper = styled(Flex).attrs({
    align: "center",
    justify: "space-between",
    direction: {
        mobileAndAbove: "column-reverse",
        tabletAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-2caf833a-0"
})([
    "position:relative;width:100%;height:280px;background-color:",
    ";",
    "{height:auto;}"
], function(param) {
    var $backgroundColor = param.$backgroundColor;
    return $backgroundColor !== null && $backgroundColor !== void 0 ? $backgroundColor : null;
}, responsiveMediaQueries.mobile);
var MessageContainer = styled(Column).withConfig({
    componentId: "sc-2caf833a-1"
})([
    "",
    "{width:100%;max-width:100%;padding:",
    ";}",
    "{height:100%;padding:",
    ";}",
    "{padding:",
    ";}",
    "{padding:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing("2x", "2x", "3x"), responsiveMediaQueries.tabletAndAbove, getSpacing(0, "5x"), responsiveMediaQueries.desktopAndAbove, getSpacing(0, "6x"), responsiveMediaQueries.xLargeAndAbove, getSpacing(0, "10x", 0, "6x"));
var StyledLink = styled(Anchor).withConfig({
    componentId: "sc-2caf833a-2"
})([
    "border-bottom:1px solid ",
    ";"
], Core.border.muted);
var StyledRow = styled(Row).attrs({
    align: "start"
}).withConfig({
    componentId: "sc-2caf833a-3"
})([
    ""
]);
var MessageImage = styled(Column).withConfig({
    componentId: "sc-2caf833a-4"
})([
    "position:relative;height:100%;width:50%;background-repeat:no-repeat;background-size:cover;background-position:left;",
    "{min-height:160px;width:100%;background-image:url(",
    ");}",
    "{background-image:url(",
    ");min-width:384px;}",
    "{background-image:url(",
    ");min-width:550px;}"
], responsiveMediaQueries.mobile, function(param) {
    var $isSSR = param.$isSSR, $images = param.$images;
    return $images.mobile ? optimizeImageUrlFillRectangle($images.mobile, $isSSR, 375, 160) : null;
}, responsiveMediaQueries.tabletAndAbove, function(param) {
    var $isSSR = param.$isSSR, $images = param.$images;
    return $images.tablet ? optimizeImageUrlFillRectangle($images.tablet, $isSSR, 384, 280) : null;
}, responsiveMediaQueries.xLargeAndAbove, function(param) {
    var $isSSR = param.$isSSR, $images = param.$images;
    return $images.xlarge ? optimizeImageUrlFillRectangle($images.xlarge, $isSSR, 550, 280, {
        resizeMethod: "fit"
    }) : null;
});
var StyledFairTradeFederationIcon = styled.img.withConfig({
    componentId: "sc-2caf833a-5"
})([
    "position:absolute;background-repeat:no-repeat;background-size:cover;background-position:left;",
    "{right:20px;bottom:20px;width:68px;height:70px;}",
    "{right:32px;bottom:32px;width:82px;height:84px;}",
    "{right:43px;bottom:32px;}",
    "{right:40px;bottom:40px;width:105px;height:108px;}"
], responsiveMediaQueries.mobile, responsiveMediaQueries.tabletAndAbove, responsiveMediaQueries.desktopAndAbove, responsiveMediaQueries.xLargeAndAbove);
